import {
  ESTADOS_LEAD_CERRADOS,
  ETAPAS,
  type EnviarMensajeInput,
  type InstanciaPublic,
  type LeadCalificarInput,
  type LeadPublic,
  type MensajePublic,
} from '@app/shared';
import { Ban, CheckCircle2, MoreVertical, Sparkles, XCircle, Zap } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import { DropdownMenu, DropdownMenuItem, DropdownMenuSeparator } from '../../../components/ui/dropdown-menu';
import { StarRating } from '../../../components/ui/star-rating';
import { useI18n } from '../../../i18n/I18nProvider';
import { cn } from '../../../lib/cn';
import { etiquetaDe, type MapaInstancias } from '../api/instancias.api';
import { fechaLarga, formatearTelefono, iniciales, mismoDia } from '../lib/formato';

import { BurbujaMensaje } from './BurbujaMensaje';
import { CalificarLead } from './CalificarLead';
import { ChipEstado, ChipInstancia } from './Chips';
import { Compositor, type CompositorHandle } from './Compositor';
import { EliminarLeadDialog } from './EliminarLeadDialog';

interface ConversacionProps {
  lead: LeadPublic;
  mensajes: MensajePublic[];
  instancias: InstanciaPublic[];
  porNombre: MapaInstancias;
  cargando: boolean;
  enviando: boolean;
  enviandoAudio: boolean;
  eliminando: boolean;
  onEnviar: (input: EnviarMensajeInput) => Promise<void>;
  onEnviarAudio: (blob: Blob, meta: { instancia?: string; fijarComoPreferida?: boolean }) => Promise<void>;
  onCerrar: (motivo: 'GANADO' | 'NO_INTERESADO') => void;
  onCambiarEtapa: (etapa: number) => void;
  onEliminar: (motivo: string) => Promise<void>;
  onCalificarSugerencia: (sugerenciaId: string, calificacion: number) => void;
  // ciclo-calidad-leads: Bueno / Basura / Compró, para mejorar los leads de Meta.
  onCalificarLead: (input: LeadCalificarInput) => void;
  calificandoLead: boolean;
  // Botón "generar ahora" (29/09/2026): salta la ventana de agrupación.
  onGenerarAhora: () => void;
  generandoAhora: boolean;
}

// Qué chat se ve cuando la persona habló por los dos números: los dos a la vez o
// uno solo. Se recuerda en el navegador (R6).
const CLAVE_VISTA = 'bandeja.vistaNumeros';
// 'ambos' o el nombre de una instancia.
type Vista = string;

function leerVista(): Vista {
  try {
    return localStorage.getItem(CLAVE_VISTA) ?? 'ambos';
  } catch {
    return 'ambos';
  }
}

// Columna derecha: cabecera del lead, la charla (por número) y el compositor.
export function Conversacion(p: ConversacionProps): JSX.Element {
  const es = useI18n();
  const cerrado = ESTADOS_LEAD_CERRADOS.includes(p.lead.estado);
  const bloqueado = p.lead.estado === 'BLOQUEADO';
  const telefono = formatearTelefono(p.lead.telefono);
  const sugerencia = p.lead.sugerenciaPendiente;
  const [vista, setVista] = useState<Vista>(leerVista);
  const [confirmarEliminar, setConfirmarEliminar] = useState(false);
  const compositorRef = useRef<CompositorHandle>(null);

  // Los números por los que hay chat, con la preferida primero (así viene del backend).
  const numeros = p.lead.instancias;
  const doble = numeros.length > 1;
  const porInstancia = useMemo(() => {
    const m = new Map<string, MensajePublic[]>();
    for (const n of numeros) m.set(n, []);
    for (const msg of p.mensajes) {
      const lista = m.get(msg.instancia);
      if (lista) lista.push(msg);
      else m.set(msg.instancia, [msg]);
    }
    return m;
  }, [p.mensajes, numeros]);

  const elegirVista = (v: Vista): void => {
    setVista(v);
    try {
      localStorage.setItem(CLAVE_VISTA, v);
    } catch {
      /* sin localStorage */
    }
  };
  // Si la vista guardada apunta a un número que esta persona no usa, se ven ambos.
  const vistaEfectiva: Vista = doble && vista !== 'ambos' && !numeros.includes(vista) ? 'ambos' : vista;
  const columnas = doble && vistaEfectiva === 'ambos' ? numeros : doble ? [vistaEfectiva] : numeros;

  const origen = p.lead.campana?.nombre ?? es.bandeja.fuentes[p.lead.fuente];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center gap-3 border-b border-border-subtle bg-surface px-4 py-2.5">
        <span
          aria-hidden
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-muted text-sm font-semibold text-text-subtle"
        >
          {iniciales(p.lead.nombre, p.lead.telefono)}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold text-text-strong">
            {p.lead.nombre ?? telefono ?? es.bandeja.sinNombre}
          </h2>
          <p className="truncate text-xs text-text-subtle">
            {telefono ?? es.bandeja.sinTelefono}
            {' · '}
            {origen}
            {p.lead.deteccionFuente !== 'NINGUNA' && ` (${es.bandeja.deteccion[p.lead.deteccionFuente]})`}
            {p.lead.adTitulo && ` · “${p.lead.adTitulo}”`}
          </p>
        </div>
        {doble && (
          <div role="tablist" aria-label={es.bandeja.numeros.vista} className="flex items-center gap-1">
            {(['ambos', ...numeros] as Vista[]).map((v) => (
              <button
                key={v}
                role="tab"
                type="button"
                aria-selected={vistaEfectiva === v}
                onClick={() => elegirVista(v)}
                className={cn(
                  'rounded-full px-2 py-0.5 text-[11px] font-medium',
                  vistaEfectiva === v
                    ? 'bg-marca-primario text-white'
                    : 'bg-surface-muted text-text-subtle hover:bg-marca-neutro',
                )}
              >
                {v === 'ambos' ? es.bandeja.numeros.ambos : etiquetaDe(p.porNombre, v)}
              </button>
            ))}
          </div>
        )}
        <CalificarLead
          calidad={p.lead.calidad}
          motivoBasura={p.lead.motivoBasura}
          guardando={p.calificandoLead}
          onCalificar={p.onCalificarLead}
        />
        <ChipEstado estado={p.lead.estado} />
        <label className="flex items-center gap-1 text-xs text-text-subtle">
          <span className="sr-only sm:not-sr-only">{es.bandeja.etapa}</span>
          <select
            value={p.lead.etapa}
            onChange={(e) => p.onCambiarEtapa(Number(e.target.value))}
            aria-label={es.bandeja.acciones.cambiarEtapa}
            className="h-8 rounded-md border border-border-strong bg-surface px-2 text-xs text-text-strong"
          >
            {ETAPAS.map((n) => (
              <option key={n} value={n}>
                {n} · {es.bandeja.etapas[n]}
              </option>
            ))}
          </select>
        </label>
        {!bloqueado && (
          <DropdownMenu
            align="right"
            triggerAriaLabel={es.bandeja.acciones.cerrar}
            trigger={
              <span className="flex h-8 w-8 items-center justify-center rounded-md text-text-subtle hover:bg-surface-muted">
                <MoreVertical size={16} />
              </span>
            }
          >
            {!cerrado && (
              <>
                <DropdownMenuItem icon={<CheckCircle2 size={14} />} onClick={() => p.onCerrar('GANADO')}>
                  {es.bandeja.acciones.cerrarGanado}
                </DropdownMenuItem>
                <DropdownMenuItem icon={<XCircle size={14} />} onClick={() => p.onCerrar('NO_INTERESADO')}>
                  {es.bandeja.acciones.cerrarNoInteresado}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem
              icon={<Ban size={14} />}
              onClick={() => setConfirmarEliminar(true)}
              variant="danger"
            >
              {es.bandeja.acciones.eliminar}
            </DropdownMenuItem>
          </DropdownMenu>
        )}
      </header>

      <div
        className={cn(
          'grid min-h-0 flex-1 bg-surface-sunken',
          // En pantalla chica no entran dos columnas: se ve la preferida y se conmuta.
          columnas.length > 1
            ? 'grid-cols-1 md:grid-cols-2 md:divide-x md:divide-border-subtle'
            : 'grid-cols-1',
        )}
      >
        {columnas.map((nombre, i) => (
          <ColumnaChat
            key={`${p.lead.id}-${nombre}`}
            ocultaEnChica={columnas.length > 1 && i > 0}
            nombre={nombre}
            instancia={p.porNombre[nombre]}
            preferida={nombre === p.lead.instanciaPreferida}
            mensajes={porInstancia.get(nombre) ?? []}
            cargando={p.cargando}
            conCabecera={doble}
          />
        ))}
      </div>

      {sugerencia && (
        <div className="mx-3 mb-2 flex items-center justify-between gap-2 rounded-md border border-marca-primario/30 bg-marca-primario/5 px-3 py-1.5 text-xs text-text-subtle">
          <span className="flex items-center gap-2">
            <Sparkles size={14} className="shrink-0 text-marca-primario" aria-hidden />
            <span>{es.bandeja.sugerencia.aviso}</span>
          </span>
          <span className="flex shrink-0 items-center gap-2">
            {/* Si Ailén ya estaba escribiendo algo propio, la sugerencia no se
                precargó sola (ver Compositor) — este botón la trae a pedido. */}
            <button
              type="button"
              onClick={() => compositorRef.current?.fijarTexto(sugerencia.texto)}
              className="rounded-md border border-marca-primario/40 px-2 py-1 text-[11px] font-medium text-marca-primario hover:bg-marca-primario/10"
            >
              {es.bandeja.sugerencia.usar}
            </button>
            <StarRating
              value={sugerencia.calificacion}
              onChange={(n) => p.onCalificarSugerencia(sugerencia.id, n)}
              label={es.bandeja.sugerencia.calificarEstrella}
            />
          </span>
        </div>
      )}
      {!sugerencia && !cerrado && !bloqueado && (
        <div className="mx-3 mb-2 flex items-center justify-between gap-2 rounded-md border border-border-subtle bg-surface-muted px-3 py-1.5 text-xs text-text-subtle">
          <span className="flex items-center gap-2">
            <Zap size={14} className="shrink-0 text-text-faint" aria-hidden />
            {/* Ventana programada (solo leads Meta Ads, D-023/025): ya está en
                camino, avisa que está esperando. Cualquier otro caso (otra
                fuente, o Meta Ads sin ventana todavía): el botón sirve igual,
                simplemente no había nada corriendo para "saltar". */}
            <span>
              {p.lead.ventanaSugerenciaVenceEn
                ? es.bandeja.sugerencia.esperando
                : es.bandeja.sugerencia.sinSugerencia}
            </span>
          </span>
          <button
            type="button"
            onClick={p.onGenerarAhora}
            disabled={p.generandoAhora}
            className="shrink-0 rounded-md bg-marca-primario px-2.5 py-1 text-[11px] font-medium text-white disabled:opacity-60"
          >
            {p.generandoAhora ? es.bandeja.sugerencia.generando : es.bandeja.sugerencia.generarAhora}
          </button>
        </div>
      )}
      <Compositor
        ref={compositorRef}
        leadId={p.lead.id}
        valorInicial={sugerencia?.texto}
        instancias={p.instancias}
        preferida={p.lead.instanciaPreferida}
        enviando={p.enviando}
        enviandoAudio={p.enviandoAudio}
        onEnviar={p.onEnviar}
        onEnviarAudio={p.onEnviarAudio}
        deshabilitado={bloqueado}
        aviso={bloqueado ? es.bandeja.compositor.bloqueado : cerrado ? es.bandeja.compositor.cerrado : null}
      />
      <EliminarLeadDialog
        open={confirmarEliminar}
        onOpenChange={setConfirmarEliminar}
        eliminando={p.eliminando}
        onConfirmar={p.onEliminar}
      />
    </div>
  );
}

// Un chat por número: cabecera con el chip (solo si hay más de uno) y las burbujas.
function ColumnaChat(p: {
  nombre: string;
  instancia: InstanciaPublic | undefined;
  preferida: boolean;
  mensajes: MensajePublic[];
  cargando: boolean;
  conCabecera: boolean;
  /** Segunda columna en pantalla chica: se oculta (queda el conmutador). */
  ocultaEnChica?: boolean;
}): JSX.Element {
  const es = useI18n();
  const finRef = useRef<HTMLDivElement>(null);
  const etiqueta = p.instancia?.etiqueta ?? p.nombre;

  useEffect(() => {
    // jsdom (tests) no implementa scrollIntoView.
    if (typeof finRef.current?.scrollIntoView === 'function') finRef.current.scrollIntoView({ block: 'end' });
  }, [p.mensajes.length, p.nombre]);

  return (
    <section
      aria-label={es.bandeja.numeros.chatPor.replace('{etiqueta}', etiqueta)}
      className={cn('flex min-h-0 flex-col', p.ocultaEnChica && 'hidden md:flex')}
    >
      {p.conCabecera && (
        <div className="flex items-center gap-2 border-b border-border-subtle bg-surface/60 px-3 py-1.5 text-xs text-text-subtle">
          <ChipInstancia
            instancia={p.instancia ?? { nombre: p.nombre, etiqueta, color: '0 0% 45%' }}
            preferida={p.preferida}
          />
          <span className="truncate">{p.instancia ? formatearTelefono(p.instancia.numero) : p.nombre}</span>
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {p.cargando && <p className="text-center text-xs text-text-faint">{es.bandeja.cargando}</p>}
        {!p.cargando && p.mensajes.length === 0 && (
          <p className="text-center text-xs text-text-faint">{es.bandeja.numeros.sinMensajes}</p>
        )}
        <ul className="flex flex-col gap-1.5">
          {p.mensajes.map((m, i) => {
            const anterior = p.mensajes[i - 1];
            const separador = !anterior || !mismoDia(anterior.timestamp, m.timestamp);
            return (
              <Fragment key={m.id}>
                {separador && (
                  <li className="my-2 text-center">
                    <span className="rounded-full bg-surface px-3 py-1 text-[11px] text-text-faint shadow-card">
                      {fechaLarga(m.timestamp)}
                    </span>
                  </li>
                )}
                <BurbujaMensaje mensaje={m} />
              </Fragment>
            );
          })}
        </ul>
        <div ref={finRef} />
      </div>
    </section>
  );
}
